import { useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import { Button } from '~/components/ui/button'

export interface ChatInputProps {
    onSend(text: string): void
    disabled?: boolean
    placeholder?: string
}

/** 채팅 입력창. Enter 전송, Shift+Enter 줄바꿈. 한글 IME 조합 중 Enter는 전송하지 않는다. */
export function ChatInput({ onSend, disabled, placeholder }: ChatInputProps) {
    const [value, setValue] = useState('')
    // IME(한글 등) 조합 중 여부. 조합 중 Enter는 전송이 아니라 글자 확정으로 처리한다.
    const composingRef = useRef(false)

    const submit = () => {
        const text = value.trim()
        if (!text || disabled) return
        onSend(text)
        // 조합 종료 후 잔여 commit이 덮어쓰지 않도록 입력폼을 확실히 비운다.
        setValue('')
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        submit()
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            // 한글 등 IME 조합 중인 Enter는 마지막 글자 확정용이므로 전송하지 않는다.
            if (composingRef.current || e.nativeEvent.isComposing) return
            e.preventDefault()
            submit()
        }
    }

    return (
        <form onSubmit={handleSubmit} className='flex items-end gap-2'>
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => {
                    composingRef.current = true
                }}
                onCompositionEnd={() => {
                    composingRef.current = false
                }}
                rows={1}
                placeholder={placeholder ?? '메시지를 입력하세요'}
                className='border-border bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-ring max-h-40 min-h-[2.5rem] flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none'
            />
            <Button type='submit' disabled={disabled || !value.trim()}>
                전송
            </Button>
        </form>
    )
}
